/* eslint-disable react/prop-types */
import React, { useEffect, useState } from "react";
import { CheckCircle2, Loader2, XCircle, FileText } from "lucide-react";

const formatBytes = (bytes) => {
  if (!bytes || bytes <= 0) return "0 KB";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const GiftCardProgressModal = ({
  isOpen,
  progressData = { loaded: 0, total: null, percent: null, isCompleted: false },
  onCancel,
  title = "Processing Gift Card",
  itemType = "Gift Card",
}) => {
  const [simulatedPercent, setSimulatedPercent] = useState(5);

  const loaded = progressData?.loaded || 0;
  const total = progressData?.total || null;
  const isCompleted = Boolean(progressData?.isCompleted);

  // Smooth progress calculation handling both known total and gzipped streaming
  useEffect(() => {
    if (!isOpen) {
      setSimulatedPercent(5);
      return;
    }

    if (isCompleted) {
      setSimulatedPercent(100);
      return;
    }

    if (loaded > 0) {
      if (total && total > 0) {
        const actualPercent = Math.min(
          95,
          Math.max(20, Math.round((loaded / total) * 100))
        );
        setSimulatedPercent(actualPercent);
      } else {
        // Logarithmic continuous scaling for gzipped streams (smoothly advances 20% -> 97%)
        const logPercent = Math.min(
          97,
          Math.round(20 + 77 * (1 - Math.exp(-loaded / 3000000)))
        );
        setSimulatedPercent((prev) => Math.max(prev, logPercent));
      }
    } else {
      // TTFB Server generation phase: creep smoothly from 5% to 20%
      const interval = setInterval(() => {
        setSimulatedPercent((prev) => (prev < 20 ? prev + 1 : prev));
      }, 150);
      return () => clearInterval(interval);
    }
  }, [isOpen, loaded, total, isCompleted]);

  if (!isOpen) return null;

  // Dynamic status & detail text
  let statusText = `Generating ${itemType} design on server...`;
  let subText = "Creating high-resolution vector PDF...";

  if (isCompleted) {
    statusText = `${itemType} generated successfully!`;
    subText = "Opening PDF print window...";
  } else if (loaded > 0) {
    statusText = `Downloading ${itemType} PDF package...`;
    if (total && total > 0) {
      subText = `${formatBytes(loaded)} / ${formatBytes(total)} (${simulatedPercent}%)`;
    } else {
      subText = `${formatBytes(loaded)} downloaded...`;
    }
  }

  const effectivePercent = isCompleted ? 100 : simulatedPercent;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-md p-4 animate-in fade-in duration-200">
      <div className="relative w-full max-w-md bg-gray-900 border border-amber-500/30 rounded-xl p-6 text-white shadow-2xl overflow-hidden">
        {/* Ambient Glow Background Effect */}
        <div className="absolute -top-12 -right-12 w-36 h-36 bg-amber-500/10 rounded-full blur-2xl pointer-events-none" />
        <div className="absolute -bottom-12 -left-12 w-36 h-36 bg-yellow-500/10 rounded-full blur-2xl pointer-events-none" />

        <div className="relative z-10 flex flex-col items-center text-center">
          {/* Animated Header Icon */}
          <div className="mb-4 flex items-center justify-center">
            {isCompleted ? (
              <div className="w-14 h-14 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-400 animate-in zoom-in-75 duration-300">
                <CheckCircle2 size={32} />
              </div>
            ) : (
              <div className="relative w-14 h-14 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-cyellow">
                <FileText size={26} className="animate-pulse" />
                <Loader2 size={44} className="absolute inset-0 m-auto animate-spin text-amber-400/60" />
              </div>
            )}
          </div>

          {/* Modal Title & Dynamic Status */}
          <h3 className="text-lg font-bold text-cwhite tracking-wide mb-1">
            {title}
          </h3>
          <p className="text-sm font-medium text-amber-300/90 mb-1">
            {statusText}
          </p>
          <p className="text-xs text-gray-400 mb-6 font-mono">
            {subText}
          </p>

          {/* Progress Bar Container */}
          <div className="w-full bg-gray-800/90 rounded-full h-3.5 p-0.5 border border-gray-700/60 shadow-inner mb-3 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-300 ease-out relative ${
                isCompleted
                  ? "bg-gradient-to-r from-emerald-500 to-teal-400 shadow-[0_0_12px_rgba(16,185,129,0.5)]"
                  : "bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-300 shadow-[0_0_12px_rgba(245,158,11,0.5)]"
              }`}
              style={{ width: `${effectivePercent}%` }}
            >
              {/* Shimmer effect inside progress bar */}
              {!isCompleted && (
                <div className="absolute inset-0 bg-white/20 animate-pulse rounded-full" />
              )}
            </div>
          </div>

          {/* Percentage & Controls */}
          <div className="w-full flex justify-between items-center text-xs font-mono text-gray-400">
            <span>{effectivePercent}% Completed</span>
            {loaded > 0 && <span>{formatBytes(loaded)}</span>}
          </div>

          {/* Optional Cancel Action */}
          {onCancel && !isCompleted && (
            <button
              onClick={onCancel}
              className="mt-6 inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-red-400 transition-colors py-1 px-3 rounded-md hover:bg-red-500/10"
            >
              <XCircle size={14} />
              Cancel Request
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GiftCardProgressModal;
